(ns motorsaif.web.pages.dashboard.requests.show
  (:require
   [goog.string :refer [format]]
   [motorsaif.string :as string]
   [motorsaif.frontend-routes :as routes]
   [motorsaif.i18n :as i18n :refer [tr]]
   [motorsaif.shared.chat :as shared-chat]
   [motorsaif.time :as time]
   [motorsaif.web.pwa :as pwa]
   [motorsaif.url :as url]
   [motorsaif.utils :as utils]
   [motorsaif.web.components :as c]
   [motorsaif.web.photos :as photos]
   [re-frame.core :as rf :refer [dispatch subscribe]]
   [reagent.core :as r]
   [reagent.ratom :as ra]))

(defn- last-msg [thread]
  (:message (last (sort-by (comp :date-time/created :message) < thread))))

(defn thread-avatar-letters [threads]
  (let [n (count threads)]
    (into [:div.thread-avatars]
          (conj (mapv (fn [{:keys [profile]}]
                        [c/avatar-letter profile {:class "xs white-border"}])
                      (take 4 threads))
                (when (> n 4)
                  [:div.avatar.letter.small.remainder.xs.white-border
                   (format "+%s" (- n 4))])))))

(defn- msg-content-preview
  "Return text or media"
  [{:keys [thread request current-owner-id]}]
  (let [{:keys [messages touches participants]} thread
        {:message/keys [body from media]
         last-ts :date-time/created} (last-msg messages)
        ;; current-owner-id @(subscribe [:current-owner-id])
        ack true
        from-id (:db/id from)
        from-current-user? (= current-owner-id from-id)
        other-participant (shared-chat/other-participant {:thread thread
                                                          :request request
                                                          :current-owner-id current-owner-id})
        other-participant-id (:db/id other-participant)
        last-access (get-in touches [other-participant-id :touched/last-access])
        seen? (when last-access (time/after? last-access last-ts))]
    [:div.content-preview
     (when media
       [:div.last-media
        (into [:div.photo-grid
               {:class (str "n-" (count media))}]
              (map (fn [itm]
                     (let [{media-id :db/id} itm
                           src (url/uploaded-media {:media itm
                                                    :size :med})]
                       ^{:key media-id}
                       [:div.media-item
                        [:img {:src src}]]))) media)
        (tr [:page.messages.media] [(count media)])])
     (when body [:div.last-msg
                 (when from-current-user?
                   [:span.tick-status
                    (cond
                      (and ack seen?)
                      [:span.read-receipt
                       [:i.fa-regular.fa-check]
                       [:i.fa-regular.fa-check]]
                      ack
                      [:span.message-ack
                       [:i.fa-regular.fa-check]])])
                 [:span.body body]])]))

(defn- chat-response
  [{:keys [profile thread-id request-id] :as argz}]
  (let [thread @(subscribe [:thread thread-id])
        {owner-id :db/id handle :user/handle} profile
        {:keys [messages]} thread
        {last-ts :date-time/created :as last-msg} (last-msg messages)
        unread-count (count @(subscribe [:unread-requester-messages thread-id]))]
    [:div.response
     {:on-click #(rf/dispatch [:common/navigate! :dash-request-chat {:request-id request-id
                                                                     :owner-id owner-id}])}
     [:div.wrap
      [:div.top.if-c
       [c/avatar-letter-with-handle {:owner profile
                                     :letter-class "med"
                                     :no-link true}]
       [:span.sep " • "]
       [:span.last-sent-time
        [c/ago-str-interval last-ts 1000]]]
      [:div.preview-wrap
       [msg-content-preview
        {:thread thread
         :current-owner-id @(subscribe [:current-owner-id])}]
       (when (> unread-count 0)
         [:span.unread.circle
          (format "%s" unread-count)])]]
     [:div
      [:i.fa-regular.fa-chevron-right]]]))

(defn business-responses [request-id]
  (let [threads @(subscribe [:requester-thread request-id])]
    [:section#business-responses.container-sm
     [:div.header
      [:div.title "Live Chat Responses"]
      [:div.subtitle
       [:span.bold (count threads)]
       (format " %s %s responded to your request"
               (string/pluralize :word "business"
                                 :suffix "es"
                                 :count (count threads))
               (string/pluralize :word "has"
                                 :plural "have"
                                 :count (count threads)))]]
     (if (seq threads)
       (let [inner-sort (fn [msgs]
                          (sort-by #(get-in % [:date-time/created]) > msgs))
             sorted-threads (sort-by (comp inner-sort :messages :thread) > threads)]
         (into [:div.responses]
               (map #(chat-response (assoc % :request-id request-id)) sorted-threads)))
       [:div.no-responses-wrap
        [:div.notifications.info
         [:div.icon
          [:i.fa-regular.fa-bell]]
         [:div.title "Notifications"]
         [:p "You will receive e-mails upon receiving new responses."]
         (condp = (pwa/pushable-state)
           :can-push
           [:<>
            [:button.btn.primary.full "Enable Instant Notifications"]]
           :ios-install-app
           [:<>
            [:p "You can enable push notifications on your iPhone by adding this app
on your home screen."]] nil)]
        [:div.no-responses
         [:div.icon
          [:i.fa-regular.fa-comment-dots]]
         [:div.title "No responses yet"]
         [:p "This is where you will receive livechats from businesses."]]])]))

(defn request-details [request-id]
  (let [request @(subscribe [:current-owner-request request-id])
        {created :date-time/created
         id :db/id
         short-id :short/id
         {:user/keys [handle]} :owner
         :request/keys [object media description category]} request
        {:car/keys [year make model]} object
        category (:tag/title category)
        unread-count (count @(subscribe [:unread-requester-parent-messages id]))
        request-status (get-in request [:request/status :db/ident])
        threads @(subscribe [:requester-thread id])
        request-type (utils/request-type->str (get-in request [:request/type :db/ident]))
        img (photos/thumbnail-src request)]
    [:section#request-details.container-sm
     [:div.top.padded
      (if img
        [:img {:src (photos/thumbnail-src request)}]
        [:div])
      [:div.info
       (when (> unread-count 0)
         [:span.unread.if-c (format "%s new" unread-count)])
       [:div.text.car (format "%s %s %s" year make model)]
       [:div.description description]
       [:div.meta.mono
        [:span.short-id.text.uc short-id]
        [:span.request-type.tag request-type]]]]
     [:div.bottom
      (when (= request-status :active)
        [:div.actions
         [:a.btn.primary.outline
          {:href
           (routes/path-for :request {:id short-id})}
          "View"]
         [:a.btn.secondary.outline
          {:href (routes/path-for :dash-edit-request {:request-id id})}
          "Edit"]])]]))

(defn root []
  (let [*route-params (subscribe [:route/parameters])
        {:keys [request-id]} @*route-params

        *loaded-once? (r/atom false)
        *loaded? (subscribe [:all-ajax-loaded? :bootstrap-data :threads/active])
        *switch (ra/reaction
                  (or @*loaded-once?
                      @*loaded?))]
    (dispatch [:ensure-user])
    (r/create-class
      {:reagent-render
       (fn []
         (when @*loaded?
           (let [request @(subscribe [:current-owner-request request-id])
                {short-id :short/id} request]
            [:<>
             [c/header-with-back (tr :page.messages.requester-threads.title)
              {:back-fn #(rf/dispatch [:common/navigate! :dash-requests])
               :subtitle
               [:div
                [:span "#"]
                [:span.uc.text.mono short-id]]}]
             [request-details request-id]
             [business-responses request-id]])))})))
